/**
 * Super-admin Settings page — Email / SMS / Payments configuration.
 *
 * - Loads /api/admin/settings → sanitised (no decrypted secrets, only last-4).
 * - On Save, PUT /api/admin/settings/{tab} with the modified fields. Blank
 *   secret inputs are treated as "preserve existing"; explicit "__clear__"
 *   sentinel clears them.
 * - Send-test buttons re-use the existing /api/admin/notifications/test path.
 */
import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  EnvelopeOpen, ChatCircleText, CreditCard, FloppyDisk, PaperPlaneTilt,
  CheckCircle, Warning, Eye, EyeSlash, Receipt as ReceiptIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { EightyGConfigCard } from "@/components/EightyGConfigCard";
import SecurityCard from "@/components/SecurityCard";

function SecretInput({ id, label, value, set, last4, isSet }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label} {isSet && <span className="text-xs text-[var(--vs-text-secondary)] ml-2">stored: <code>…{last4}</code></span>}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          data-testid={id}
          type={reveal ? "text" : "password"}
          placeholder={isSet ? "(leave blank to keep existing)" : "Paste secret here…"}
          value={value || ""}
          onChange={(e) => set(e.target.value)}
          className="font-mono"
        />
        <Button type="button" variant="outline" size="sm" onClick={() => setReveal((r) => !r)} data-testid={`${id}-reveal`}>
          {reveal ? <EyeSlash size={14} /> : <Eye size={14} />}
        </Button>
        {isSet && (
          <Button type="button" variant="outline" size="sm" onClick={() => set("__clear__")} data-testid={`${id}-clear`}>Clear</Button>
        )}
      </div>
    </div>
  );
}

function ProviderBadge({ live, name }) {
  return (
    <Badge variant="outline" className={live ? "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10" : "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10"}>
      {live ? <CheckCircle size={11} className="mr-1" weight="duotone" /> : <Warning size={11} className="mr-1" weight="duotone" />}
      {live ? `LIVE · ${name}` : "Dry-run"}
    </Badge>
  );
}

export default function Settings() {
  const [data, setData] = useState(null);
  const [emailForm, setEmailForm] = useState({
    provider: "logger", api_key: "", from_addr: "",
    smtp_host: "", smtp_port: 587, smtp_username: "", smtp_password: "",
    smtp_use_tls: true, smtp_use_ssl: false,
  });
  const [smsForm, setSmsForm] = useState({ provider: "logger", account_sid: "", auth_token: "", from_number: "" });
  const [payForm, setPayForm] = useState({ provider: "none", api_key: "", use_emergent_proxy: false });
  const [providers, setProviders] = useState({ email: "logger", sms: "logger", email_live: false, sms_live: false });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const [s, p] = await Promise.all([
      api.get("/admin/settings"),
      api.get("/admin/notifications/providers"),
    ]);
    setData(s.data);
    setProviders(p.data);
    setEmailForm({
      provider: s.data.email.provider,
      api_key: "",
      from_addr: s.data.email.from_addr || "",
      smtp_host: s.data.email.smtp_host || "",
      smtp_port: s.data.email.smtp_port || 587,
      smtp_username: s.data.email.smtp_username || "",
      smtp_password: "",
      smtp_use_tls: s.data.email.smtp_use_tls !== false,
      smtp_use_ssl: !!s.data.email.smtp_use_ssl,
    });
    setSmsForm({ provider: s.data.sms.provider, account_sid: "", auth_token: "", from_number: s.data.sms.from_number || "" });
    setPayForm({ provider: s.data.payment.provider, api_key: "", use_emergent_proxy: !!s.data.payment.use_emergent_proxy });
  };

  useEffect(() => { refresh().catch(() => {}); }, []);

  const saveEmail = async () => {
    setBusy(true);
    try {
      const body = { provider: emailForm.provider, from_addr: emailForm.from_addr };
      if (emailForm.api_key) body.api_key = emailForm.api_key;
      if (emailForm.provider === "smtp") {
        body.smtp_host = emailForm.smtp_host;
        body.smtp_port = Number(emailForm.smtp_port);
        body.smtp_username = emailForm.smtp_username;
        body.smtp_use_tls = emailForm.smtp_use_tls;
        body.smtp_use_ssl = emailForm.smtp_use_ssl;
        if (emailForm.smtp_password) body.smtp_password = emailForm.smtp_password;
      }
      await api.put("/admin/settings/email", body);
      toast.success("Email settings saved");
      await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const saveSms = async () => {
    setBusy(true);
    try {
      const body = { provider: smsForm.provider, from_number: smsForm.from_number };
      if (smsForm.account_sid) body.account_sid = smsForm.account_sid;
      if (smsForm.auth_token) body.auth_token = smsForm.auth_token;
      await api.put("/admin/settings/sms", body);
      toast.success("SMS settings saved");
      await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const savePayment = async () => {
    setBusy(true);
    try {
      const body = { provider: payForm.provider, use_emergent_proxy: payForm.use_emergent_proxy };
      if (payForm.api_key) body.api_key = payForm.api_key;
      await api.put("/admin/settings/payment", body);
      toast.success("Payment settings saved");
      await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const sendTest = async (channel) => {
    const to = window.prompt(channel === "email" ? "Send test email to:" : "Send test SMS to (E.164):");
    if (!to) return;
    try {
      const res = await api.post("/admin/notifications/test", { channel, to, subject: "VSMS test", body: "VSMS test notification — please ignore." });
      const live = res.data?.delivery && res.data.delivery !== "logged";
      toast.success(live ? `Sent via ${res.data.delivery}` : "Logged (dry-run)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  if (!data) {
    return (
      <DashboardLayout title="Settings" subtitle="Configure email, SMS, and payment providers — encrypted at rest.">
        <div className="vs-card p-8 text-center text-[var(--vs-text-secondary)]" data-testid="settings-loading">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Settings" subtitle="Configure email, SMS, and payment providers — encrypted at rest. Keys override backend/.env values.">
      <Tabs defaultValue="email" className="w-full">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="email" data-testid="tab-email"><EnvelopeOpen size={14} className="mr-1.5" />Email</TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms"><ChatCircleText size={14} className="mr-1.5" />SMS</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment"><CreditCard size={14} className="mr-1.5" />Payments</TabsTrigger>
          <TabsTrigger value="80g" data-testid="tab-80g"><ReceiptIcon size={14} className="mr-1.5" />80G receipts</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">Reminders</TabsTrigger>
          <TabsTrigger value="sibling" data-testid="tab-sibling">Sibling discount</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
        </TabsList>

        {/* ---------- Email ---------- */}
        <TabsContent value="email">
          <div className="vs-card p-6 max-w-2xl space-y-5" data-testid="email-settings-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow">Status</div>
                <div className="mt-1 flex items-center gap-2"><ProviderBadge live={providers.email_live} name={providers.email} /></div>
              </div>
              <Button variant="outline" size="sm" onClick={() => sendTest("email")} data-testid="email-send-test"><PaperPlaneTilt size={12} className="mr-1.5" />Send test</Button>
            </div>
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <Select value={emailForm.provider} onValueChange={(v) => setEmailForm({ ...emailForm, provider: v })}>
                <SelectTrigger data-testid="email-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="logger">Dry-run (logs only — no delivery)</SelectItem>
                  <SelectItem value="resend">Resend</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="smtp">SMTP (custom server)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {emailForm.provider !== "smtp" && (
              <SecretInput id="email-api-key" label="API key" value={emailForm.api_key} set={(v) => setEmailForm({ ...emailForm, api_key: v })} last4={data.email.api_key_last4} isSet={data.email.api_key_set} />
            )}
            {emailForm.provider === "smtp" && (
              <div className="space-y-3 p-4 bg-[var(--vs-bg-subtle)] rounded-md border border-[var(--vs-border)]">
                <div className="eyebrow">SMTP server</div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor="smtp-host">Host</Label>
                    <Input id="smtp-host" data-testid="smtp-host" placeholder="smtp.gmail.com" value={emailForm.smtp_host} onChange={(e) => setEmailForm({ ...emailForm, smtp_host: e.target.value })} className="font-mono" />
                  </div>
                  <div>
                    <Label htmlFor="smtp-port">Port</Label>
                    <Input id="smtp-port" data-testid="smtp-port" type="number" min="1" max="65535" value={emailForm.smtp_port} onChange={(e) => setEmailForm({ ...emailForm, smtp_port: e.target.value })} className="font-mono" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="smtp-username">Username</Label>
                  <Input id="smtp-username" data-testid="smtp-username" placeholder="noreply@example.com" value={emailForm.smtp_username} onChange={(e) => setEmailForm({ ...emailForm, smtp_username: e.target.value })} className="font-mono" />
                </div>
                <SecretInput id="smtp-password" label="Password" value={emailForm.smtp_password} set={(v) => setEmailForm({ ...emailForm, smtp_password: v })} last4={data.email.smtp_password_last4} isSet={data.email.smtp_password_set} />
                <div className="flex items-center gap-6 text-sm">
                  <label className="flex items-center gap-2">
                    <input data-testid="smtp-use-tls" type="checkbox" checked={emailForm.smtp_use_tls} onChange={(e) => setEmailForm({ ...emailForm, smtp_use_tls: e.target.checked })} className="accent-[var(--vs-secondary)]" />
                    Use STARTTLS (port 587)
                  </label>
                  <label className="flex items-center gap-2">
                    <input data-testid="smtp-use-ssl" type="checkbox" checked={emailForm.smtp_use_ssl} onChange={(e) => setEmailForm({ ...emailForm, smtp_use_ssl: e.target.checked })} className="accent-[var(--vs-secondary)]" />
                    Use SSL (port 465)
                  </label>
                </div>
                <p className="text-[11px] text-[var(--vs-text-secondary)]">For Gmail use port 587 + STARTTLS with an App Password. For Office 365 use smtp.office365.com:587 + STARTTLS.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email-from">From address</Label>
              <Input id="email-from" data-testid="email-from-addr" placeholder="VSMS <no-reply@vidyaseva.org>" value={emailForm.from_addr} onChange={(e) => setEmailForm({ ...emailForm, from_addr: e.target.value })} />
            </div>
            <Button disabled={busy} onClick={saveEmail} data-testid="email-save" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]"><FloppyDisk size={14} className="mr-1.5" />Save email settings</Button>
          </div>
        </TabsContent>

        {/* ---------- SMS ---------- */}
        <TabsContent value="sms">
          <div className="vs-card p-6 max-w-2xl space-y-5" data-testid="sms-settings-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow">Status</div>
                <div className="mt-1 flex items-center gap-2"><ProviderBadge live={providers.sms_live} name={providers.sms} /></div>
              </div>
              <Button variant="outline" size="sm" onClick={() => sendTest("sms")} data-testid="sms-send-test"><PaperPlaneTilt size={12} className="mr-1.5" />Send test</Button>
            </div>
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <Select value={smsForm.provider} onValueChange={(v) => setSmsForm({ ...smsForm, provider: v })}>
                <SelectTrigger data-testid="sms-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="logger">Dry-run (logs only — no delivery)</SelectItem>
                  <SelectItem value="twilio">Twilio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <SecretInput id="sms-account-sid" label="Account SID" value={smsForm.account_sid} set={(v) => setSmsForm({ ...smsForm, account_sid: v })} last4={data.sms.account_sid_last4} isSet={data.sms.account_sid_set} />
            <SecretInput id="sms-auth-token" label="Auth Token" value={smsForm.auth_token} set={(v) => setSmsForm({ ...smsForm, auth_token: v })} last4={data.sms.auth_token_last4} isSet={data.sms.auth_token_set} />
            <div className="space-y-1.5">
              <Label htmlFor="sms-from">From number (E.164)</Label>
              <Input id="sms-from" data-testid="sms-from-number" placeholder="+15551234567" value={smsForm.from_number} onChange={(e) => setSmsForm({ ...smsForm, from_number: e.target.value })} />
            </div>
            <Button disabled={busy} onClick={saveSms} data-testid="sms-save" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]"><FloppyDisk size={14} className="mr-1.5" />Save SMS settings</Button>
          </div>
        </TabsContent>

        {/* ---------- Payments ---------- */}
        <TabsContent value="payment">
          <div className="vs-card p-6 max-w-2xl space-y-5" data-testid="payment-settings-card">
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <Select value={payForm.provider} onValueChange={(v) => setPayForm({ ...payForm, provider: v })}>
                <SelectTrigger data-testid="payment-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (parent payments disabled)</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <SecretInput id="payment-api-key" label="Stripe Secret Key" value={payForm.api_key} set={(v) => setPayForm({ ...payForm, api_key: v })} last4={data.payment.api_key_last4} isSet={data.payment.api_key_set} />
            <div className="flex items-start gap-3 vs-card p-4 bg-[var(--vs-bg-subtle)]">
              <Switch
                id="payment-proxy"
                data-testid="payment-proxy-toggle"
                checked={payForm.use_emergent_proxy}
                onCheckedChange={(v) => setPayForm({ ...payForm, use_emergent_proxy: v })}
              />
              <div className="flex-1">
                <Label htmlFor="payment-proxy">Use Emergent test proxy</Label>
                <p className="text-xs text-[var(--vs-text-secondary)] mt-1">
                  Routes Stripe API calls through Emergent's dev test proxy. Use this with the seeded <code>sk_test_emergent</code> key for QA. For production, paste your own Stripe key and turn this off.
                </p>
              </div>
            </div>
            <Button disabled={busy} onClick={savePayment} data-testid="payment-save" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]"><FloppyDisk size={14} className="mr-1.5" />Save payment settings</Button>
          </div>
        </TabsContent>

        {/* ---------- 80G receipt config ---------- */}
        <TabsContent value="80g">
          <EightyGConfigCard />
        </TabsContent>

        <TabsContent value="reminders"><RemindersCard /></TabsContent>
        <TabsContent value="sibling"><SiblingDiscountCard /></TabsContent>
        <TabsContent value="security"><SecurityCard /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}


function RemindersCard() {
  const [cfg, setCfg] = useState({ enabled: false, schedule_time_utc: "09:00", channels: { email: true, sms: true, whatsapp: true }, last_run_at: null, last_run_summary: null });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/reminders/config").then((r) => setCfg({ ...cfg, ...r.data }));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggleChannel = (k) => setCfg((c) => ({ ...c, channels: { ...c.channels, [k]: !c.channels[k] } }));

  const save = async () => {
    setBusy(true);
    try { await api.post("/reminders/config", cfg); toast.success("Saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reminders/run-now");
      toast.success(`Sent: email=${r.data.email} sms=${r.data.sms} whatsapp=${r.data.whatsapp} (skipped ${r.data.skipped})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="vs-card p-5 space-y-4">
      <div>
        <h3 className="font-medium">Automated fee-dues reminders</h3>
        <p className="text-sm text-[var(--vs-text-secondary)]">
          A daily sweep at the configured UTC time. For every unpaid fee period it dispatches the chosen channels to the parent. Each channel resolves its provider from the existing Email / SMS / WhatsApp settings — DRY-RUN mode just logs a notification row.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="rem-enabled" />
        <span className="text-sm">Enable scheduler</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Daily run time (UTC, 24-hour)</Label>
          <Input data-testid="rem-time" placeholder="09:00" value={cfg.schedule_time_utc || ""} onChange={(e) => setCfg({ ...cfg, schedule_time_utc: e.target.value })} className="bg-white font-mono" />
          <div className="text-[10px] text-[var(--vs-text-secondary)] mt-1">IST = UTC + 05:30. Tip: 03:30 UTC ≈ 09:00 IST.</div>
        </div>
        <div className="space-y-2">
          <Label>Channels</Label>
          <div className="flex gap-3">
            {["email", "sms", "whatsapp"].map((k) => (
              <label key={k} className="flex items-center gap-1.5 text-sm capitalize">
                <input type="checkbox" data-testid={`rem-ch-${k}`} checked={!!cfg.channels?.[k]} onChange={() => toggleChannel(k)} /> {k}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button data-testid="rem-save" onClick={save} disabled={busy}>Save</Button>
        <Button data-testid="rem-run-now" variant="outline" onClick={runNow} disabled={busy}>Run sweep now</Button>
      </div>
      {cfg.last_run_at && (
        <div className="text-xs text-[var(--vs-text-secondary)] bg-[var(--vs-bg-subtle)] border border-[var(--vs-border)]/40 rounded p-2 font-mono" data-testid="rem-last-run">
          Last run: {cfg.last_run_at} — {JSON.stringify(cfg.last_run_summary)}
        </div>
      )}
    </div>
  );
}


function SiblingDiscountCard() {
  const [cfg, setCfg] = useState({ enabled: false, pct: 10, min_children: 2 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/sibling-discount").then((r) => setCfg(r.data));
  }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/settings/sibling-discount", cfg); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="vs-card p-5 space-y-4">
      <div>
        <h3 className="font-medium">Sibling discount auto-rule</h3>
        <p className="text-sm text-[var(--vs-text-secondary)]">
          When 2+ active students share the same parent phone / email / parent_father+mother, the fee-breakdown screen automatically applies a sibling discount on the post-scholarship subtotal. The discount appears as a separate line item.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="sib-enabled" />
        <span className="text-sm">Enable sibling discount</span>
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div>
          <Label>Discount %</Label>
          <Input data-testid="sib-pct" type="number" min="0" max="100" value={cfg.pct} onChange={(e) => setCfg({ ...cfg, pct: Number(e.target.value) || 0 })} className="bg-white font-mono" />
        </div>
        <div>
          <Label>Minimum children</Label>
          <Input data-testid="sib-min" type="number" min="2" max="10" value={cfg.min_children} onChange={(e) => setCfg({ ...cfg, min_children: Number(e.target.value) || 2 })} className="bg-white font-mono" />
        </div>
      </div>
      <Button data-testid="sib-save" onClick={save} disabled={busy}>Save</Button>
    </div>
  );
}
